// A-0007.03 Addendum (2026-04-24): simplified schema. Mirrors Server's Joi
// schema and Mind's assistant_doc consumer. Platform builder reads/writes this
// exact shape.
//
// Key changes from the original PR-3 shape:
//   - `_id` removed (Redis key `assistant:{org}:{sub}:{assistant_id}` is the
//     only identity anchor).
//   - `assistant_name` renamed to `assistant_id`. `assistant_id` is an
//     auto-generated, user-invisible slug (Server derives it from `title`).
//     It is never an editable input in the UI.
//   - `opcodes_v2` removed — there is now one `opcodes` list, rich objects
//     only (`{opcode_id, purpose, description, input_mapping}`).
//   - `assistant_token` always required by the A2A contract, but write-only on
//     the wire: reads redact it and answer `has_token` instead, so a loaded
//     assistant carries `has_token` and an EMPTY `assistant_token`.

export interface OpcodeEntry {
  opcode_id: string;
  purpose: string;
  description: string;
  input_mapping: Record<string, string>; // opcode input name -> "context|propensity|intent|entity.field"
  /** Present on list/get when `enrich=opcodes` — read-only, not persisted on assistant. */
  title?: string | null;
  category?: string | null;
}

export type EntitySchemaPropertyType =
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'array'
  | 'object';

export interface EntitySchemaProperty {
  type: EntitySchemaPropertyType;
  pattern?: string;
  description?: string;
  enum?: string[];
  // loosely typed — JSON Schema has many more keys we don't need to surface today
  [extra: string]: unknown;
}

export interface EntitySchema {
  type: 'object';
  properties: Record<string, EntitySchemaProperty>;
  required: string[];
  additionalProperties: boolean;
}

export interface TypedInputDef {
  context_keys: string[];
  propensity_keys: string[];
  intent: {
    format: 'text' | 'structured';
    routing_mode: 'intent_match' | 'default_only';
  };
  entity_schema: EntitySchema;
}

export interface KnowledgeConn { collection_id: string; description: string; }
export interface DataConn { connection_id: string; description: string; }

export interface InstructionsBlock {
  system_prompt: string | null;
  guidelines: string | null;
  content_policy: string | null;
}

export interface ChannelsConfig {
  mode: 'restricted' | 'open';
  authorized_channels: string[];
}

export interface MemoryConfig {
  strategy: 'window' | 'summary' | 'full' | 'hybrid';
  max_turns: number;
  max_tokens: number;
  ttl: number;
  employee_memory: boolean;
  governance: { retain: string[]; forget: string[] };
}

export interface EscalationRule { condition: string; action: string; }

export interface GovernanceBlock {
  allowed_actions: string[];
  denied_actions: string[];
  escalation: EscalationRule[];
  boundaries: string;
}

export interface Assistant {
  assistant_id: string;
  title: string;
  description: string;
  persona: string;
  version: string;
  status: 'active' | 'inactive';
  disabled: boolean;
  org_id: string;
  sub_org_id: string;
  /**
   * Write-only. Set it to rotate the token; a doc loaded from the server never
   * carries it back (see `has_token`).
   */
  assistant_token: string;
  /** Read-only: whether the stored doc has a token. Never persisted. */
  has_token?: boolean;
  opcodes: OpcodeEntry[];
  default_opcode: string | null;
  typed_input: TypedInputDef;
  knowledge: KnowledgeConn[];
  data_connections: DataConn[];
  instructions: InstructionsBlock;
  channels: ChannelsConfig;
  memory: MemoryConfig;
  governance: GovernanceBlock;
  createdBy: string;
  updatedBy: string | null;
  created: string;
  updated: string;
}

// Back-compat: channel records (from the channels service) are still referenced
// by config-mode screens. Kept separate from ChannelsConfig (auth policy).
export interface Channel {
  _id: string;
  topic: string;
  created: string;
  assistantId?: string;
  address?: string;
  type?: string;
}

// Opcode-definition item (used by selection pickers). Post-feedback
// 2026-04-24_10-53, there is no user-facing type distinction between
// opcodes — the backend may still store them in different places (classic
// vs. json-authored), but the UI treats every opcode as just "an opcode".
export interface Opcode {
  name: string;
  opcode_id: string;
  description?: string;
  created?: string | Date;
  updated?: string | Date;
  createdBy?: string;
  updatedBy?: string;
  // Rich opcodes carry an `inputs` array of {name,type,description,...}
  inputs?: Array<{ name: string; type?: string; description?: string; required?: boolean }>;
}

export interface OpCodeAssistant {
  name: string;
  default: string; // 'true' | 'false'
  id?: string;
  description?: string;
}

export interface ComboBoxItem {
  label: string;
  value: string;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
}

// Factory: default Assistant form state for the wizard. Mirrors the Joi defaults
// applied on Server so the wizard can start empty and still pass validation
// after the required identity fields are filled.
//
// NOTE: `assistant_id` is NOT seeded. Server auto-generates it from `title`
// on CREATE. The UI only learns the real id from the create response.
export const INPUT_MAPPING_PATTERN = /^(context|propensity|intent|entity)\.[A-Za-z_][A-Za-z0-9_]*$/;

export function makeEmptyAssistant(org_id: string, sub_org_id: string): Assistant {
  return {
    assistant_id: '',
    title: '',
    description: '',
    persona: '',
    version: '1.0.0',
    status: 'active',
    disabled: false,
    org_id,
    sub_org_id,
    assistant_token: '',
    opcodes: [],
    default_opcode: null,
    typed_input: {
      context_keys: [],
      propensity_keys: [],
      intent: {
 format: 'text',
routing_mode: 'default_only'
},
      entity_schema: {
        type: 'object',
        properties: {},
        required: [],
        additionalProperties: false,
      },
    },
    knowledge: [],
    data_connections: [],
    instructions: {
 system_prompt: null,
guidelines: null,
content_policy: null
},
    channels: {
 mode: 'restricted',
authorized_channels: []
},
    memory: {
      strategy: 'window',
      max_turns: 50,
      max_tokens: 4000,
      ttl: 86400,
      employee_memory: false,
      governance: {
 retain: [],
forget: []
},
    },
    governance: {
      allowed_actions: [],
      denied_actions: [],
      escalation: [],
      boundaries: '',
    },
    createdBy: '',
    updatedBy: null,
    created: '',
    updated: '',
  };
}
